<script setup lang="ts">
/**
 * UiField — wrapper для label + input + hint + error (inbox-117).
 *
 * Default slot — UiInput / UiSelect / любой form-element. Когда error
 * непустой, .field получает класс .has-error, который красит вложенный
 * input красной рамкой через :slotted().
 *
 * CSS портом из docs/mockups/auth-login.html + auth-signup.html
 * (.field / .field-label / .field-hint / .field-error / .has-error).
 *
 * Usage:
 *   <UiField label="Email" :error="errors.email" required>
 *     <UiInput v-model="email" type="email" />
 *   </UiField>
 *
 *   <UiField label="Slug" hint="3–64 символа, латиница и цифры">
 *     <UiInput v-model="slug" />
 *   </UiField>
 */

interface Props {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Привязывается к <label for=""> для accessibility. */
  for?: string;
}

withDefaults(defineProps<Props>(), {
  label: '',
  hint: '',
  error: '',
  required: false,
  for: '',
});
</script>

<template>
  <div class="field" :class="{ 'has-error': !!error }">
    <label v-if="label" class="field-label" :for="$props.for || undefined">
      {{ label }}<span v-if="required" class="req" aria-hidden="true">*</span>
    </label>
    <slot />
    <div v-if="hint && !error" class="field-hint">{{ hint }}</div>
    <div v-if="error" class="field-error" role="alert">{{ error }}</div>
  </div>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.field-label {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--fg-muted);
}

.req {
  color: var(--danger);
  margin-left: 2px;
}

.field-hint {
  font-size: 11.5px;
  color: var(--fg-dim);
  line-height: 1.45;
}

.field-error {
  font-size: 11.5px;
  color: var(--danger);
  line-height: 1.45;
}

/* Красная рамка на input/select/textarea внутри слота когда есть error. */
.field.has-error :slotted(.input),
.field.has-error :slotted(input),
.field.has-error :slotted(select),
.field.has-error :slotted(textarea) {
  border-color: var(--danger);
}
</style>
